import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceGoogleSheet,
  UserImportDataSourceMSExcel,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { CF, FS } from '@dadohr/shared/versions';
import {
  Button,
  DialogActions,
  DialogContent,
  TextField
} from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { AlertBox } from '../../../../../../components/AlertBox';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { Label } from '../../../../../../components/CustomInputLabel';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import {
  FormRow,
  FormSection
} from '../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../components/InfoBox';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { lightMdToJsx } from '../../../../../../services/formatter';
import { toGoogleSheetsLink } from '../../../../../../services/google';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { getGoogleSheetsCurrentUser } from '../../../../../Org/pages/Integrations/integrations/googleSheets/service';
import {
  customFieldToUserImportMapping,
  CustomImportMultiMappingForm2
} from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

export enum SpreadSheetType {
  EXCEL = 'EXCEL',
  GOOGLE_SHEETS = 'GOOGLE_SHEETS'
}

const createEmptyUserImportMapping = (): UserImportMapping => ({
  dadoKey: '',
  otherKey: ''
});

const getMeta = (spreadSheetType: SpreadSheetType) =>
  spreadSheetType === SpreadSheetType.GOOGLE_SHEETS
    ? IMPORT_META.GOOGLE
    : IMPORT_META.EXCEL;

export const NameInput = ({
  name,
  onChange = () => null
}: {
  name: string;
  onChange?: (name: string) => any;
}) => {
  const { t } = useUnsafeTranslation();
  const isEditable = !!onChange;

  return (
    <>
      <Label
        label={t('Source Name')}
        subLabel={
          isEditable
            ? t('Will be used as title for the Google Sheet')
            : undefined
        }
      />
      <TextField
        disabled={!isEditable}
        fullWidth
        value={name}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  );
};

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'email',
    otherKey: 'Work Email'
  },
  {
    dadoKey: 'personalEmail',
    otherKey: 'Personal Email'
  },
  {
    dadoKey: 'firstName',
    otherKey: 'First Name'
  },
  {
    dadoKey: 'preferredName',
    otherKey: 'Preferred Name'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'Last Name'
  },
  {
    dadoKey: 'managerId',
    otherKey: 'Manager Email'
  },
  {
    dadoKey: 'location',
    otherKey: 'Location'
  },
  {
    dadoKey: 'role',
    otherKey: 'Role'
  },
  {
    dadoKey: 'department',
    otherKey: 'Department'
  },
  {
    dadoKey: 'team',
    otherKey: 'Team'
  },
  {
    dadoKey: 'hireDate',
    otherKey: 'First day at work'
  },
  {
    dadoKey: 'terminationDate',
    otherKey: 'Last day at work'
  }
];

// Temporarily disabled
const WILL_EXISTING_USERS_BE_SEEDED = false;

export const createGoogleSheetViewButton = (
  d: Doc<UserImportDataSourceGoogleSheet>
) => ({
  href: toGoogleSheetsLink(d.data.docId, d.data.sheetId),
  disabled: d.data.status === UserImportDataSourceStatus.PENDING
});

export const createExcelViewButton = (d: Doc<UserImportDataSourceMSExcel>) => ({
  href: d.data.webUrl || '#',
  disabled: d.data.status === UserImportDataSourceStatus.PENDING
});

const getEmptyDataSource = (
  spreadSheetType: SpreadSheetType,
  orgId: string,
  userId: string,
  customFields: CustomUserField[],
  name: string
) => {
  switch (spreadSheetType) {
    case SpreadSheetType.GOOGLE_SHEETS:
      const googleDatasource: UserImportDataSourceGoogleSheet = {
        orgId: orgId,
        type: UserImportSourceType.GOOGLE_SHEET,
        status: UserImportDataSourceStatus.PENDING,
        detailedStatus: '',
        lastUpdate: null,
        createdBy: userId,
        createdAt: now(),
        customMappings: [
          ...DEFAULT_MAPPINGS,
          ...customFields
            .filter((f) => !f.restricted)
            .map((c) => customFieldToUserImportMapping(c))
        ],
        docId: '',
        sheetId: '',
        name
      };
      return googleDatasource;
    case SpreadSheetType.EXCEL:
      const excelDataSource: UserImportDataSourceMSExcel = {
        type: UserImportSourceType.MS_EXCEL,
        driveItemId: '',
        status: UserImportDataSourceStatus.PENDING,
        detailedStatus: '',
        lastUpdate: null,
        createdBy: userId,
        createdAt: now(),
        customMappings: [
          ...DEFAULT_MAPPINGS,
          ...customFields
            .filter((f) => !f.restricted)
            .map((c) => customFieldToUserImportMapping(c))
        ],
        name,
        orgId,
        driveId: null,
        webUrl: null,
        sheetId: null,
        tableId: null
      };
      return excelDataSource;
  }
};
const SpreadsheetCreateNewSheetFlow = ({
  orgId,
  userId,
  onClose,
  customFields,
  spreadSheetType
}: {
  orgId: string;
  userId: string;
  onClose: () => void;
  customFields: CustomUserField[];
  spreadSheetType: SpreadSheetType;
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [isDirty, setIsDirty] = useState(false);
  const initName = `Dado User List`;
  const [state, setState] = useState<
    Doc<UserImportDataSourceGoogleSheet | UserImportDataSourceMSExcel>
  >({
    id: shortid(),
    collection: FS.userImportDataSources,
    data: getEmptyDataSource(
      spreadSheetType,
      orgId,
      userId,
      customFields,
      initName
    )
  });
  const meta = getMeta(spreadSheetType);
  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={meta.logoUrl} />
        </FormRow>
        {WILL_EXISTING_USERS_BE_SEEDED && (
          <FormRow>
            <InfoBox>
              {t(
                'Existing users in Dado will be added to the created Google Sheet.'
              )}
            </InfoBox>
          </FormRow>
        )}
        <FormSection>
          <NameInput
            name={state.data.name}
            onChange={(name) =>
              setState((d) => ({
                ...d,
                data: {
                  ...d.data,
                  name: name
                }
              }))
            }
          />
        </FormSection>
        <FormSection>
          <DateFormatPicker
            value={state.data.dateFormats || []}
            onChange={(formats) => {
              setIsDirty(true);
              setState((x) => ({
                ...x,
                data: { ...x.data, dateFormats: formats }
              }));
            }}
          />
        </FormSection>
        <CustomImportMultiMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          enableSave={isDirty}
          fieldTerm={t('column')}
          integrationName="Google Sheet"
          userFields={userFields}
          value={{
            main: state.data.customMappings,
            others: state.data.inferredOtherUsers?.map((v) => v.customMappings)
          }}
          onChange={(nextVal) => {
            setIsDirty(true);
            setState((x) => ({
              ...x,
              data: {
                ...x.data,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((x) => ({
                    customMappings: x
                  })) || []
              }
            }));
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            await setDoc(state);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

const SpreadsheetUseExistingSheetFlowSelectStep = ({
  orgId,
  onClose,
  onSelect,
  spreadSheetType
}: {
  orgId: string;
  onClose: () => void;
  onSelect: (fileData: SpreadsheetFileData) => void;
  spreadSheetType: SpreadSheetType;
}) => {
  const { t } = useUnsafeTranslation();
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const meta = getMeta(spreadSheetType);
  const urlPlaceholder =
    spreadSheetType === SpreadSheetType.GOOGLE_SHEETS
      ? 'https://docs.google.com/spreadsheets/d/...'
      : 'https://yourcompany-my.sharepoint.com/:x:/...';
  const checkGoogleSheetsFileUrl = async () => {
    if (!url.startsWith('https://docs.google.com/spreadsheets/d/')) {
      setErr(`This doesn't seem to be a spreadsheet URL`);
      return;
    }
    const docId = url.match(
      '^https://docs.google.com/spreadsheets/d/(.+?)(/|$)'
    )?.[1];
    const sheetId =
      url.match(
        '^https://docs.google.com/spreadsheets/d/.+#gid=(.+?)(&|$)'
      )?.[1] || '0';
    console.log(docId, sheetId);

    try {
      const [info, user] = await Promise.all([
        callFirebaseFunction<
          | {
              status: 'OK';
              ids: {
                docId: string;
                sheetId: string;
              };
              titles: {
                docTitle: string;
                sheetTitle: string;
              };
            }
          | {
              status: 'ERROR';
              err: string;
            }
        >('integrations_googleSheets-getSheetInformation', {
          orgId,
          docId,
          sheetId
        }),
        getGoogleSheetsCurrentUser(orgId).then((d) => d.email || '')
      ]);

      console.log(info);
      if (info.status === 'OK') {
        const googleFileData: GoogleSheetFileData = {
          type: SpreadSheetType.GOOGLE_SHEETS,
          name: info.titles.docTitle,
          ...info.ids,
          user
        };
        onSelect(googleFileData);
      } else {
        const errText =
          info.err === 'Forbidden'
            ? t('Please make sure {{ user }} has write access to this file', {
                user
              })
            : info.err === 'Not Found'
            ? t('File not found')
            : t('Unable to verify file access');
        setErr(errText);
      }
    } catch (err) {
      setErr(t('Unable to verify file access'));
    }
  };

  const checkExcelFileUrl = async () => {
    type Response =
      | {
          status: 'OK';
          driveItemId: string;
          filename: string;
          columns: string[];
          driveId: string;
        }
      | {
          status: 'ERROR';
          message: string;
        };

    try {
      const response = await callFirebaseFunction<Response>(
        CF.microsoft.files.getExcelFileInfoFromWebUrl,
        { orgId, webUrl: url }
      );
      if (response.status === 'ERROR') {
        console.error(
          `Error retrieveing Excel document infos: ${response.message}`
        );
        setErr(t('Unable to verify file access'));
        return;
      }
      const { driveItemId, columns, filename, driveId } = response;
      const name =
        filename.substring(0, filename.lastIndexOf('.')) || 'Dado User List';
      if (driveItemId) {
        const excelFileData: ExcelFileData = {
          driveItemId,
          columns,
          name,
          driveId,
          sheetId: null,
          webUrl: url,
          type: SpreadSheetType.EXCEL
        };
        onSelect(excelFileData);
      }
    } catch (err) {
      setErr(t('Unable to verify file access'));
    }
  };

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={meta.logoUrl} />
        </FormRow>
        <p>
          {t(
            `Please enter the URL of the spreadsheet you'd like to import from.`
          )}
        </p>
        <TextField
          fullWidth
          label="URL"
          placeholder={urlPlaceholder}
          variant="outlined"
          value={url}
          onChange={(ev) => setUrl(ev.target.value)}
          error={!!err}
          helperText={err}
        />
      </DialogContent>

      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>

        <ButtonWithPromise
          pending={t('Checking...')}
          variant="contained"
          color="primary"
          onClick={async () => {
            if (!url) {
              setErr(t('Please select a file URL'));
              return;
            }

            switch (spreadSheetType) {
              case SpreadSheetType.GOOGLE_SHEETS:
                await checkGoogleSheetsFileUrl();
                break;
              case SpreadSheetType.EXCEL:
                await checkExcelFileUrl();
                break;
            }
          }}
        >
          {t('Verify file access')}
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};
const getImportSourceDocumentFromFileData = (
  fileData: SpreadsheetFileData,
  orgId: string,
  userId: string
): Doc<SpreadSheetImportSource> => {
  const getData = (): SpreadSheetImportSource => {
    switch (fileData.type) {
      case SpreadSheetType.GOOGLE_SHEETS:
        return {
          orgId: orgId,
          type: UserImportSourceType.GOOGLE_SHEET,
          status: UserImportDataSourceStatus.OK,
          detailedStatus: '',
          lastUpdate: null,
          createdBy: userId,
          createdAt: now(),
          customMappings: [createEmptyUserImportMapping()],
          docId: fileData.docId,
          sheetId: fileData.sheetId,
          name: fileData.name
        };
      case SpreadSheetType.EXCEL:
        const result: UserImportDataSourceMSExcel = {
          orgId: orgId,
          type: UserImportSourceType.MS_EXCEL,
          status: UserImportDataSourceStatus.OK,
          detailedStatus: '',
          lastUpdate: null,
          createdBy: userId,
          createdAt: now(),
          customMappings: fileData.columns.map((c) => ({
            dadoKey: '',
            otherKey: c
          })),
          driveItemId: fileData.driveItemId,
          webUrl: fileData.webUrl,
          sheetId: fileData.sheetId,
          driveId: fileData.driveId,
          name: fileData.name,
          tableId: null
        };
        return result;
    }
  };

  const data = getData();

  return {
    id: shortid(),
    collection: FS.userImportDataSources,
    data: data
  };
};

type SpreadSheetImportSource =
  | UserImportDataSourceGoogleSheet
  | UserImportDataSourceMSExcel;

const SpreadsheetUseExistingSheetFlowConfigureStep = ({
  orgId,
  userId,
  onClose,
  customFields,
  fileData
}: {
  orgId: string;
  userId: string;
  onClose: () => void;
  customFields: CustomUserField[];
  fileData: SpreadsheetFileData;
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [isDirty, setIsDirty] = useState(false);
  const [state, setState] = useState<Doc<SpreadSheetImportSource>>(
    getImportSourceDocumentFromFileData(fileData, orgId, userId)
  );
  const meta = getMeta(fileData.type);

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={meta.logoUrl} />
        </FormRow>
        {
          <FormRow>
            <AlertBox color="blue">
              {lightMdToJsx(
                t(
                  'Please make sure that **{{ user }}** has **Editor** permissions for this document',
                  {
                    user:
                      fileData.type === SpreadSheetType.GOOGLE_SHEETS
                        ? fileData.user
                        : 'Dado MS Integration User'
                  }
                )
              )}
            </AlertBox>
          </FormRow>
        }
        <FormSection>
          <NameInput name={state.data.name} />
        </FormSection>
        <FormSection>
          <DateFormatPicker
            value={state.data.dateFormats || []}
            onChange={(formats) => {
              setIsDirty(true);
              setState((x) => ({
                ...x,
                data: { ...x.data, dateFormats: formats }
              }));
            }}
          />
        </FormSection>
        <CustomImportMultiMappingForm2
          additionalCompositeFields={['$FULL_NAME$']}
          enableSave={isDirty}
          fieldTerm={t('column')}
          integrationName="Google Sheet"
          userFields={userFields}
          value={{
            main: state.data.customMappings,
            others: state.data.inferredOtherUsers?.map((v) => v.customMappings)
          }}
          onChange={(nextVal) => {
            setIsDirty(true);
            setState((x) => ({
              ...x,
              data: {
                ...x.data,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((x) => ({
                    customMappings: x
                  })) || []
              }
            }));
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            await setDoc(state);
            onClose();
          }}
        >
          {t('Add import source')}
        </Button>
      </DialogActions>
    </>
  );
};

type ISpreadsheetFileData<T extends SpreadSheetType> = {
  type: T;
  name: string;
};

type GoogleSheetFileData = ISpreadsheetFileData<SpreadSheetType.GOOGLE_SHEETS> & {
  docId: string;
  sheetId: string;
  user: string;
};

type ExcelFileData = ISpreadsheetFileData<SpreadSheetType.EXCEL> & {
  driveItemId: string;
  webUrl: string;
  columns: string[];
  driveId: string;
  sheetId: string | null;
};

type SpreadsheetFileData = GoogleSheetFileData | ExcelFileData;

const SpreadsheetUseExistingSheetFlow = ({
  orgId,
  userId,
  onClose,
  customFields,
  spreadSheetType
}: {
  orgId: string;
  userId: string;
  onClose: () => void;
  customFields: CustomUserField[];
  spreadSheetType: SpreadSheetType;
}) => {
  const [step, setStep] = useState<'SELECT' | 'CONFIGURE'>('SELECT');
  const [fileData, setFileData] = useState<SpreadsheetFileData>();

  if (step === 'CONFIGURE' && fileData) {
    return (
      <SpreadsheetUseExistingSheetFlowConfigureStep
        orgId={orgId}
        userId={userId}
        onClose={onClose}
        fileData={fileData}
        customFields={customFields}
      />
    );
  }

  return (
    <SpreadsheetUseExistingSheetFlowSelectStep
      spreadSheetType={spreadSheetType}
      orgId={orgId}
      onClose={onClose}
      onSelect={(nextFileData) => {
        setFileData(nextFileData);
        setStep('CONFIGURE');
      }}
    />
  );
};

export const SpreadsheetSetupFlow = ({
  orgId,
  onClose,
  customFields,
  spreadSheetType
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
  spreadSheetType: SpreadSheetType;
}) => {
  // check if integration is enabled
  // if no -> go to oauth flo
  // if yes -> tell what's gonna happen, create the spreadsheet

  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();
  const [flow, setFlow] = useState<'NEW' | 'EXISTING' | null>(null);
  const meta = getMeta(spreadSheetType);
  return (
    <>
      {flow === null && (
        <>
          <DialogContent>
            <FormRow>
              <Img maxHeight={32} src={meta.logoUrl} />
            </FormRow>
            <FlexContainer fullWidth>
              <Button
                onClick={() => setFlow('NEW')}
                variant="contained"
                color="primary"
              >
                {t('Create a new import spreadsheet')}
              </Button>
              <Button
                onClick={() => setFlow('EXISTING')}
                variant="contained"
                color="primary"
              >
                {t('Use an existing spreadsheet')}
              </Button>
            </FlexContainer>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
          </DialogActions>
        </>
      )}
      {flow === 'NEW' && (
        <SpreadsheetCreateNewSheetFlow
          orgId={orgId}
          userId={userId}
          onClose={onClose}
          customFields={customFields}
          spreadSheetType={spreadSheetType}
        />
      )}

      {flow === 'EXISTING' && (
        <SpreadsheetUseExistingSheetFlow
          orgId={orgId}
          userId={userId}
          onClose={onClose}
          customFields={customFields}
          spreadSheetType={spreadSheetType}
        />
      )}
    </>
  );
};

export const ImportSourceRowGoogleSheet = ({
  d
}: {
  d: Doc<UserImportDataSourceGoogleSheet>;
}) => {
  const meta = IMPORT_META.GOOGLE;
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      logoUrl={meta.smallLogoUrl}
      name={d.data.name || meta.name}
      viewButton={{
        href: toGoogleSheetsLink(d.data.docId, d.data.sheetId),
        disabled: d.data.status === UserImportDataSourceStatus.PENDING
      }}
    />
  );
};

export const ImportSourceRowExcel = ({
  d
}: {
  d: Doc<UserImportDataSourceMSExcel>;
}) => {
  const meta = IMPORT_META.EXCEL;
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      logoUrl={meta.smallLogoUrl}
      name={d.data.name || meta.name}
      viewButton={{
        href: d.data.webUrl || '#',
        disabled: d.data.status === UserImportDataSourceStatus.PENDING
      }}
    />
  );
};
